import TrackerService from '@/admin/tracker/tracker.service';
import AdMenuService from '@/core/application-dictionary/components/Menu/menu.service';
import TranslationService from '@/locale/translation.service';
import { AccountStoreModule as accountStore, AccountStoreModule } from '@/shared/config/store/account-store';
import { AppStoreModule as appStore } from '@/shared/config/store/app-store';
import { PermissionStoreModule as permissionStore } from '@/shared/config/store/permission-store';
import { TranslationStoreModule as translationStore } from '@/shared/config/store/translation-store';
import axios from 'axios';
import VueRouter from 'vue-router';
import { Store } from 'vuex';
import DynamicWindowService from '@/core/application-dictionary/components/DynamicWindow/dynamic-window.service';
import { WindowStoreModule as windowStore } from "@/shared/config/store/window-store";

const cubeTokenProviderUrl = 'api/cubejs-token';

export default class AccountService {
  private sysConfigService: DynamicWindowService;

  constructor(
    private store: Store<any>,
    private commonService: (baseApiUrl: string) => DynamicWindowService,
    private translationService: TranslationService,
    private trackerService: TrackerService,
    private menuService: AdMenuService,
    private router: VueRouter
  ) {
    this.sysConfigService = commonService('api/c-sys-configs');
  }

  public get token() {
    return localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
  }

  public get cubejsToken() {
    return localStorage.getItem('cubejsToken') || sessionStorage.getItem('cubejsToken');
  }

  public async init(): Promise<void> {
    if (!accountStore.userIdentity && !accountStore.logon && this.token) {
      await this.retrieveAccount();
    }
    this.retrieveProfiles();
  }

  public hasToken() {
    return this.token !== null;
  }

  public retrieveProfiles(): void {
    axios.get('management/info').then(res => {
      if (res.data && res.data.activeProfiles) {
        accountStore.setRibbonOnProfiles(res.data['display-ribbon-on-profiles']);
        accountStore.setActiveProfiles(res.data['activeProfiles']);
      }
    });
  }

  public async retrieveAccount(): Promise<void> {
    accountStore.authenticate();
    
    try {
      let response = await axios.get('api/account');
      const account = response.data;

      if (account) {
        response = await this.commonService('api/ad-users').find(account.id);
        accountStore.setUserDetails(response);

        response = await axios.get('api/accesses');
        await accountStore.setGrantedResources(response.data);
        const grantedOrgsCount = accountStore.grantedOrgs.size;

        const routes = await this.menuService.retrieve();
        
        await permissionStore.updateRoutes(routes.data);
        this.router.addRoutes(permissionStore.dynamicRoutes);

        if (translationStore.language !== account.langKey) {
          await translationStore.setLanguage(account.langKey);
        }

        accountStore.setAuthenticated(account);

        // Initializing CubeJS client.
        try {
          const cubeJsHost = await this.retrieveCubeJsHost();
          const cubeJsToken = await this.retrieveCubeJsToken();
          await appStore.setCubeJsApi({ host: cubeJsHost, token: cubeJsToken });
        } catch (err) {
          console.warn('Failed initializing CubeJS API', err);
        }
        const requestedUrl = sessionStorage.getItem('requested-url');
        const requestedName= sessionStorage.getItem('module-name');

        if (requestedUrl) {
          this.router.replace(requestedUrl);
          sessionStorage.removeItem('requested-url');
        } else if(requestedName) {
          this.redirectByModuleName();
        } else if (accountStore.authorities.has('ROLE_ADMIN') && grantedOrgsCount > 2) {
          appStore.setSelectRole(true);
        } else {
          this.router.replace('/');
        }
      } else {
        console.error('Account details is not found.');
        this.router.replace('/redirect');
        sessionStorage.removeItem('requested-url');
        accountStore.logout();
      }
      this.translationService.refreshTranslation(translationStore.language);
    } catch (err) {
      console.error('Error validating account', err);
      this.router.replace('/redirect');
      accountStore.logout();
    }
  }

  retrieveCubeJsHost(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.sysConfigService
        .retrieve({
          criteriaQuery: ['active.equals=true', 'code.equals=CUBE_JS_HOST'],
          paginationQuery: {
            page: 0,
            size: 1,
            sort: ['id'],
          },
        })
        .then(res => {
          if (res.data.length) {
            resolve(res.data[0].value as string);
          } else {
            resolve(null);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  retrieveCubeJsToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.commonService(`${cubeTokenProviderUrl}`)
        .find(accountStore.account.id)
        .then(res => {
          sessionStorage.setItem('cubejsToken', res);
          resolve(res)
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  private async redirectByModuleName(): Promise<void> {

    let moduleName= sessionStorage.getItem('module-name');
    let menuAction: string= "";
    let menuId= await this.commonService("api/ad-menus")
            .retrieve({ criteriaQuery : [ 'path.equals=' + moduleName ]})
            .then(res => {
              menuAction= res.data[0].action;
              if(res.data.length > 0) return res.data[0].id ;
              return null;
            }).catch(e => {
                console.log(e);
            })
        
    let targetUrl: string= await this.commonService('/api/ad-menus/full-path').find(menuId);
    let filterParam= sessionStorage.getItem('requested-param');
    let filterValue= sessionStorage.getItem('requested-value');
    const timestamp = Date.now();
      
    if(filterParam && filterValue){
      windowStore.setWatchlistQuery(filterParam + ".equals=" + filterValue)
      .then(() => {
        this.router.replace(targetUrl + "?t=" + `${timestamp}`);
      });
    }else {
      this.router.replace(targetUrl + "?t=" + `${timestamp}`);
    }

    sessionStorage.removeItem('module-name');

    if(menuAction == "WINDOW"){
      sessionStorage.removeItem('requested-param');
      sessionStorage.removeItem('requested-value');
    }
    
  }

  /**
   * Checks whether a user has any authority accessing a route.
   * @param authorities Route meta authorities.
   */
  public hasAnyAuthority(authorities: any): boolean {
    if (!this.authenticated || !this.userAuthorities) {
      return false;
    }

    if (typeof authorities === 'string') {
      authorities = [authorities];
    }
    return authorities.some((authority: string) => this.userAuthorities.has(authority));
  }

  public get authenticated(): boolean {
    return accountStore.authenticated;
  }

  public get userAuthorities(): Set<string> {
    return accountStore.authorities;
  }
}
